import { useCallback, useEffect, useMemo, useState } from 'react';

import type { ScheduleSummary } from '@/types/schedule';

import type { AdvancedCronEditableScope } from '../types/advancedCronMigration';
import {
  isAdvancedCronSchedule,
  needsCronTimeFrameConfirm,
  needsCronTimezoneBootstrap,
  scheduleNeedsTimeFrameInCron,
} from '../utils/advancedCronTimezoneBootstrap';

export interface UseAdvancedCronMigrationInput {
  schedule: ScheduleSummary | null;
  scheduleType: string;
  cronTimeZone: string;
  cronExpression: string;
}

export interface UseAdvancedCronMigrationResult {
  isMigrationActive: boolean;
  editableScope: AdvancedCronEditableScope;
  timeframeUnconfirmed: boolean;
  readyToSave: boolean;
  showStoredUtcExpression: boolean;
  confirmTimezone: () => void;
  confirmTimeframe: () => void;
  notifyTimezoneChanged: () => void;
}

function initialScope(
  schedule: ScheduleSummary | null,
  scheduleType: string
): AdvancedCronEditableScope {
  if (scheduleType !== 'cron' || !schedule || !isAdvancedCronSchedule(schedule)) {
    return 'timezone-and-timeframe';
  }
  if (needsCronTimezoneBootstrap(schedule)) {
    return 'timezone-pending-confirmation';
  }
  if (needsCronTimeFrameConfirm(schedule)) {
    return 'timeframe-pending-confirmation';
  }
  return 'timezone-and-timeframe';
}

export function useAdvancedCronMigration(
  input: UseAdvancedCronMigrationInput
): UseAdvancedCronMigrationResult {
  const { schedule, scheduleType, cronTimeZone, cronExpression } = input;

  const [editableScope, setEditableScope] = useState<AdvancedCronEditableScope>(() =>
    initialScope(schedule, scheduleType)
  );
  const [timeframeUnconfirmed, setTimeframeUnconfirmed] = useState(
    () => initialScope(schedule, scheduleType) === 'timeframe-pending-confirmation'
  );

  const scheduleKey = schedule ? `${schedule.baseId}:${schedule.environmentId}` : undefined;

  // Reset when dialog opens a different schedule (row key only — not every field refresh)
  useEffect(() => {
    const next = initialScope(schedule, scheduleType);
    setEditableScope(next);
    setTimeframeUnconfirmed(next === 'timeframe-pending-confirmation');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: reset on schedule row change only
  }, [scheduleKey, scheduleType]);

  const needsTimeFrameInCron = useMemo(
    () => scheduleNeedsTimeFrameInCron(cronExpression, cronTimeZone.trim() || 'UTC'),
    [cronExpression, cronTimeZone]
  );

  const isMigrationActive = editableScope !== 'timezone-and-timeframe';

  const confirmTimezone = useCallback(() => {
    if (needsTimeFrameInCron) {
      setEditableScope('timeframe-pending-confirmation');
      setTimeframeUnconfirmed(true);
    } else {
      setEditableScope('timezone-and-timeframe');
      setTimeframeUnconfirmed(false);
    }
  }, [needsTimeFrameInCron]);

  const confirmTimeframe = useCallback(() => {
    setTimeframeUnconfirmed(false);
    setEditableScope('timezone-and-timeframe');
  }, []);

  const notifyTimezoneChanged = useCallback(() => {
    if (editableScope === 'timeframe-pending-confirmation') {
      setTimeframeUnconfirmed(true);
    }
  }, [editableScope]);

  const readyToSave = editableScope === 'timezone-and-timeframe';

  const showStoredUtcExpression =
    editableScope === 'timezone-pending-confirmation' ||
    editableScope === 'timeframe-pending-confirmation';

  return {
    isMigrationActive,
    editableScope,
    timeframeUnconfirmed,
    readyToSave,
    showStoredUtcExpression,
    confirmTimezone,
    confirmTimeframe,
    notifyTimezoneChanged,
  };
}
